import { useState, useEffect } from 'react';
// @mui
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';


export default function MeetingTimezoneSelector({ 
  value, 
  onChange,
}) {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 1분마다 업데이트
    
    return () => clearInterval(timer);
  }, []);
  
  // 선택된 타임존의 현재 시간 표시
  const getTimeInTimezone = (timezone) => {
    try {
      return new Intl.DateTimeFormat('ko-KR', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(currentTime);
    } catch (error) {
      return '';
    }
  };
  
  return (
    <Box>
      <TextField
        select
        fullWidth
        label="시간대 선택"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: theme.palette.common.black,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.common.black,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.common.black,
          },
        }}
      >
        {TIMEZONES.map((timezone) => (
          <MenuItem key={timezone.value} value={timezone.value} sx={{ ... timezone.value == 'Asia/Seoul' && { backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08) } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <Typography variant="body2">
                {timezone.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {getTimeInTimezone(timezone.value)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

// ko.xml에서 추출한 모든 타임존 (한글 이름 포함) - 지역별 정렬
export const TIMEZONES = [
  // 아시아
  { value: 'Asia/Gaza', label: '가자', offset: '+02:00' },
  { value: 'Asia/Nicosia', label: '니코시아', offset: '+02:00' },
  { value: 'Asia/Beirut', label: '베이루트', offset: '+02:00' },
  { value: 'Asia/Jerusalem', label: '예루살렘', offset: '+02:00' },
  { value: 'Asia/Famagusta', label: '파마구스타', offset: '+02:00' },
  { value: 'Asia/Hebron', label: '헤브론', offset: '+02:00' },
  { value: 'Asia/Damascus', label: '다마스쿠스', offset: '+03:00' },
  { value: 'Asia/Riyadh', label: '리야드', offset: '+03:00' },
  { value: 'Asia/Baghdad', label: '바그다드', offset: '+03:00' },
  { value: 'Asia/Bahrain', label: '바레인', offset: '+03:00' },
  { value: 'Asia/Aden', label: '아덴', offset: '+03:00' },
  { value: 'Asia/Amman', label: '암만', offset: '+03:00' },
  { value: 'Asia/Qatar', label: '카타르', offset: '+03:00' },
  { value: 'Asia/Kuwait', label: '쿠웨이트', offset: '+03:00' },
  { value: 'Asia/Dubai', label: '두바이', offset: '+04:00' },
  { value: 'Asia/Muscat', label: '무스카트', offset: '+04:00' },
  { value: 'Asia/Baku', label: '바쿠', offset: '+04:00' },
  { value: 'Asia/Yerevan', label: '예레반', offset: '+04:00' },
  { value: 'Asia/Tbilisi', label: '트빌리시', offset: '+04:00' },
  { value: 'Asia/Dushanbe', label: '두샨베', offset: '+05:00' },
  { value: 'Asia/Samarkand', label: '사마르칸트', offset: '+05:00' },
  { value: 'Asia/Ashgabat', label: '아슈하바트', offset: '+05:00' },
  { value: 'Asia/Aqtau', label: '아크타우', offset: '+05:00' },
  { value: 'Asia/Atyrau', label: '아티라우', offset: '+05:00' },
  { value: 'Asia/Aqtobe', label: '악토브', offset: '+05:00' },
  { value: 'Asia/Almaty', label: '알마티', offset: '+05:00' },
  { value: 'Asia/Yekaterinburg', label: '예카테린부르크', offset: '+05:00' },
  { value: 'Asia/Oral', label: '오랄', offset: '+05:00' },
  { value: 'Asia/Karachi', label: '카라치', offset: '+05:00' },
  { value: 'Asia/Qostanay', label: '코스타나이', offset: '+05:00' },
  { value: 'Asia/Qyzylorda', label: '키질로르다', offset: '+05:00' },
  { value: 'Asia/Tashkent', label: '타슈켄트', offset: '+05:00' },
  { value: 'Asia/Dhaka', label: '다카', offset: '+06:00' },
  { value: 'Asia/Bishkek', label: '비슈케크', offset: '+06:00' },
  { value: 'Asia/Omsk', label: '옴스크', offset: '+06:00' },
  { value: 'Asia/Urumqi', label: '우루무치', offset: '+06:00' },
  { value: 'Asia/Thimphu', label: '팀부', offset: '+06:00' },
  { value: 'Asia/Novosibirsk', label: '노보시비르스크', offset: '+07:00' },
  { value: 'Asia/Novokuznetsk', label: '노보쿠즈네츠크', offset: '+07:00' },
  { value: 'Asia/Barnaul', label: '바르나울', offset: '+07:00' },
  { value: 'Asia/Bangkok', label: '방콕', offset: '+07:00' },
  { value: 'Asia/Vientiane', label: '비엔티안', offset: '+07:00' },
  { value: 'Asia/Saigon', label: '사이공', offset: '+07:00' },
  { value: 'Asia/Jakarta', label: '자카르타', offset: '+07:00' },
  { value: 'Asia/Krasnoyarsk', label: '크라스노야르스크', offset: '+07:00' },
  { value: 'Asia/Tomsk', label: '톰스크', offset: '+07:00' },
  { value: 'Asia/Pontianak', label: '폰티아나크', offset: '+07:00' },
  { value: 'Asia/Phnom_Penh', label: '프놈펜', offset: '+07:00' },
  { value: 'Asia/Hovd', label: '호브드', offset: '+07:00' },
  { value: 'Asia/Manila', label: '마닐라', offset: '+08:00' },
  { value: 'Asia/Makassar', label: '마카사르', offset: '+08:00' },
  { value: 'Asia/Macau', label: '마카오', offset: '+08:00' },
  { value: 'Asia/Brunei', label: '브루나이', offset: '+08:00' },
  { value: 'Asia/Shanghai', label: '상하이', offset: '+08:00' },
  { value: 'Asia/Singapore', label: '싱가포르', offset: '+08:00' },
  { value: 'Asia/Ulaanbaatar', label: '울란바토르', offset: '+08:00' },
  { value: 'Asia/Irkutsk', label: '이르쿠츠크', offset: '+08:00' },
  { value: 'Asia/Kuala_Lumpur', label: '쿠알라룸푸르', offset: '+08:00' },
  { value: 'Asia/Kuching', label: '쿠칭', offset: '+08:00' },
  { value: 'Asia/Taipei', label: '타이베이', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: '홍콩', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: '도쿄', offset: '+09:00' },
  { value: 'Asia/Dili', label: '딜리', offset: '+09:00' },
  { value: 'Asia/Seoul', label: '서울', offset: '+09:00' },
  { value: 'Asia/Yakutsk', label: '야쿠츠크', offset: '+09:00' },
  { value: 'Asia/Jayapura', label: '자야푸라', offset: '+09:00' },
  { value: 'Asia/Chita', label: '치타', offset: '+09:00' },
  { value: 'Asia/Pyongyang', label: '평양', offset: '+09:00' },
  { value: 'Asia/Khandyga', label: '한디가', offset: '+09:00' },
  { value: 'Asia/Vladivostok', label: '블라디보스토크', offset: '+10:00' },
  { value: 'Asia/Ust-Nera', label: '우스티네라', offset: '+10:00' },
  { value: 'Asia/Magadan', label: '마가단', offset: '+11:00' },
  { value: 'Asia/Sakhalin', label: '사할린', offset: '+11:00' },
  { value: 'Asia/Srednekolymsk', label: '스레드네콜림스크', offset: '+11:00' },
  { value: 'Asia/Anadyr', label: '아나디리', offset: '+12:00' },
  { value: 'Asia/Kamchatka', label: '캄차카', offset: '+12:00' },
  { value: 'Asia/Tehran', label: '테헤란', offset: '+3:30' },
  { value: 'Asia/Kabul', label: '카불', offset: '+4:30' },
  { value: 'Asia/Colombo', label: '콜롬보', offset: '+5:30' },
  { value: 'Asia/Calcutta', label: '콜카타', offset: '+5:30' },
  { value: 'Asia/Katmandu', label: '카트만두', offset: '+5:45' },
  { value: 'Asia/Rangoon', label: '랑군', offset: '+6:30' },
  
  // 유럽
  { value: 'Europe/Guernsey', label: '건지', offset: '+00:00' },
  { value: 'Europe/Dublin', label: '더블린', offset: '+00:00' },
  { value: 'Europe/London', label: '런던', offset: '+00:00' },
  { value: 'Europe/Lisbon', label: '리스본', offset: '+00:00' },
  { value: 'Europe/Isle_of_Man', label: '맨섬', offset: '+00:00' },
  { value: 'Europe/Jersey', label: '저지', offset: '+00:00' },
  { value: 'Europe/Rome', label: '로마', offset: '+01:00' },
  { value: 'Europe/Luxembourg', label: '룩셈부르크', offset: '+01:00' },
  { value: 'Europe/Ljubljana', label: '류블랴나', offset: '+01:00' },
  { value: 'Europe/Madrid', label: '마드리드', offset: '+01:00' },
  { value: 'Europe/Monaco', label: '모나코', offset: '+01:00' },
  { value: 'Europe/Malta', label: '몰타', offset: '+01:00' },
  { value: 'Europe/Warsaw', label: '바르샤바', offset: '+01:00' },
  { value: 'Europe/Vatican', label: '바티칸', offset: '+01:00' },
  { value: 'Europe/Berlin', label: '베를린', offset: '+01:00' },
  { value: 'Europe/Belgrade', label: '베오그라드', offset: '+01:00' },
  { value: 'Europe/Budapest', label: '부다페스트', offset: '+01:00' },
  { value: 'Europe/Busingen', label: '뷔지겐', offset: '+01:00' },
  { value: 'Europe/Bratislava', label: '브라티슬라바', offset: '+01:00' },
  { value: 'Europe/Brussels', label: '브뤼셀', offset: '+01:00' },
  { value: 'Europe/Vienna', label: '비엔나', offset: '+01:00' },
  { value: 'Europe/Sarajevo', label: '사라예보', offset: '+01:00' },
  { value: 'Europe/San_Marino', label: '산마리노', offset: '+01:00' },
  { value: 'Europe/Skopje', label: '스코페', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: '스톡홀름', offset: '+01:00' },
  { value: 'Europe/Andorra', label: '안도라', offset: '+01:00' },
  { value: 'Europe/Amsterdam', label: '암스테르담', offset: '+01:00' },
  { value: 'Europe/Oslo', label: '오슬로', offset: '+01:00' },
  { value: 'Europe/Zagreb', label: '자그레브', offset: '+01:00' },
  { value: 'Europe/Gibraltar', label: '지브롤터', offset: '+01:00' },
  { value: 'Europe/Zurich', label: '취리히', offset: '+01:00' },
  { value: 'Europe/Copenhagen', label: '코펜하겐', offset: '+01:00' },
  { value: 'Europe/Tirane', label: '티라나', offset: '+01:00' },
  { value: 'Europe/Vaduz', label: '파두츠', offset: '+01:00' },
  { value: 'Europe/Paris', label: '파리', offset: '+01:00' },
  { value: 'Europe/Podgorica', label: '포드고리차', offset: '+01:00' },
  { value: 'Europe/Prague', label: '프라하', offset: '+01:00' },
  { value: 'Europe/Riga', label: '리가', offset: '+02:00' },
  { value: 'Europe/Mariehamn', label: '마리에함', offset: '+02:00' },
  { value: 'Europe/Bucharest', label: '부쿠레슈티', offset: '+02:00' },
  { value: 'Europe/Vilnius', label: '빌니우스', offset: '+02:00' },
  { value: 'Europe/Sofia', label: '소피아', offset: '+02:00' },
  { value: 'Europe/Athens', label: '아테네', offset: '+02:00' },
  { value: 'Europe/Kaliningrad', label: '칼리닌그라드', offset: '+02:00' },
  { value: 'Europe/Chisinau', label: '키시나우', offset: '+02:00' },
  { value: 'Europe/Kiev', label: '키예프', offset: '+02:00' },
  { value: 'Europe/Tallinn', label: '탈린', offset: '+02:00' },
  { value: 'Europe/Helsinki', label: '헬싱키', offset: '+02:00' },
  { value: 'Europe/Moscow', label: '모스크바', offset: '+03:00' },
  { value: 'Europe/Minsk', label: '민스크', offset: '+03:00' },
  { value: 'Europe/Volgograd', label: '볼고그라트', offset: '+03:00' },
  { value: 'Europe/Simferopol', label: '심페로폴', offset: '+03:00' },
  { value: 'Europe/Istanbul', label: '이스탄불', offset: '+03:00' },
  { value: 'Europe/Kirov', label: '키로프', offset: '+03:00' },
  { value: 'Europe/Saratov', label: '사라토프', offset: '+04:00' },
  { value: 'Europe/Samara', label: '사마라', offset: '+04:00' },
  { value: 'Europe/Astrakhan', label: '아스트라한', offset: '+04:00' },
  { value: 'Europe/Ulyanovsk', label: '울리야노프스크', offset: '+04:00' },
  
  // 미주
  { value: 'America/Godthab', label: '고드호프', offset: '-02:00' },
  { value: 'America/Noronha', label: '노롱야', offset: '-02:00' },
  { value: 'America/Scoresbysund', label: '스코레스바이선드', offset: '-02:00' },
  { value: 'America/Argentina/La_Rioja', label: '라 리오하', offset: '-03:00' },
  { value: 'America/Recife', label: '레시페', offset: '-03:00' },
  { value: 'America/Argentina/Rio_Gallegos', label: '리오 가예고스', offset: '-03:00' },
  { value: 'America/Maceio', label: '마세이오', offset: '-03:00' },
  { value: 'America/Mendoza', label: '멘도사', offset: '-03:00' },
  { value: 'America/Montevideo', label: '몬테비데오', offset: '-03:00' },
  { value: 'America/Miquelon', label: '미클롱', offset: '-03:00' },
  { value: 'America/Bahia', label: '바히아', offset: '-03:00' },
  { value: 'America/Belem', label: '벨렘', offset: '-03:00' },
  { value: 'America/Buenos_Aires', label: '부에노스 아이레스', offset: '-03:00' },
  { value: 'America/Argentina/San_Luis', label: '산루이스', offset: '-03:00' },
  { value: 'America/Santarem', label: '산타렘', offset: '-03:00' },
  { value: 'America/Santiago', label: '산티아고', offset: '-03:00' },
  { value: 'America/Argentina/San_Juan', label: '산후안', offset: '-03:00' },
  { value: 'America/Argentina/Salta', label: '살타', offset: '-03:00' },
  { value: 'America/Sao_Paulo', label: '상파울루', offset: '-03:00' },
  { value: 'America/Araguaina', label: '아라과이나', offset: '-03:00' },
  { value: 'America/Asuncion', label: '아순시온', offset: '-03:00' },
  { value: 'America/Argentina/Ushuaia', label: '우수아이아', offset: '-03:00' },
  { value: 'America/Cayenne', label: '카옌', offset: '-03:00' },
  { value: 'America/Catamarca', label: '카타마르카', offset: '-03:00' },
  { value: 'America/Cordoba', label: '코르도바', offset: '-03:00' },
  { value: 'America/Argentina/Tucuman', label: '투쿠만', offset: '-03:00' },
  { value: 'America/Paramaribo', label: '파라마리보', offset: '-03:00' },
  { value: 'America/Fortaleza', label: '포르탈레자', offset: '-03:00' },
  { value: 'America/Coyhaique', label: '푼타아레나스', offset: '-03:00' },
  { value: 'America/Jujuy', label: '후후이', offset: '-03:00' },
  { value: 'America/Guyana', label: '가이아나', offset: '-04:00' },
  { value: 'America/Guadeloupe', label: '과들루프', offset: '-04:00' },
  { value: 'America/Goose_Bay', label: '구즈베이', offset: '-04:00' },
  { value: 'America/Grenada', label: '그레나다', offset: '-04:00' },
  { value: 'America/Glace_Bay', label: '글라스베이', offset: '-04:00' },
  { value: 'America/Dominica', label: '도미니카', offset: '-04:00' },
  { value: 'America/La_Paz', label: '라파스', offset: '-04:00' },
  { value: 'America/Lower_Princes', label: '로워 프린스 쿼터', offset: '-04:00' },
  { value: 'America/Manaus', label: '마나우스', offset: '-04:00' },
  { value: 'America/Marigot', label: '마리곳', offset: '-04:00' },
  { value: 'America/Martinique', label: '마티니크', offset: '-04:00' },
  { value: 'America/Montserrat', label: '몬세라트', offset: '-04:00' },
  { value: 'America/Moncton', label: '몽턴', offset: '-04:00' },
  { value: 'America/Barbados', label: '바베이도스', offset: '-04:00' },
  { value: 'America/Boa_Vista', label: '보아 비스타', offset: '-04:00' },
  { value: 'America/Blanc-Sablon', label: '블랑 사블롱', offset: '-04:00' },
  { value: 'America/Santo_Domingo', label: '산토도밍고', offset: '-04:00' },
  { value: 'America/St_Barthelemy', label: '생바르텔레미', offset: '-04:00' },
  { value: 'America/St_Lucia', label: '세인트루시아', offset: '-04:00' },
  { value: 'America/St_Vincent', label: '세인트빈센트', offset: '-04:00' },
  { value: 'America/St_Kitts', label: '세인트키츠', offset: '-04:00' },
  { value: 'America/St_Thomas', label: '세인트토마스', offset: '-04:00' },
  { value: 'America/Aruba', label: '아루바', offset: '-04:00' },
  { value: 'America/Antigua', label: '안티과', offset: '-04:00' },
  { value: 'America/Anguilla', label: '앙귈라', offset: '-04:00' },
  { value: 'America/Caracas', label: '카라카스', offset: '-04:00' },
  { value: 'America/Campo_Grande', label: '캄포 그란데', offset: '-04:00' },
  { value: 'America/Cuiaba', label: '쿠이아바', offset: '-04:00' },
  { value: 'America/Curacao', label: '퀴라소', offset: '-04:00' },
  { value: 'America/Kralendijk', label: '크라렌디즈크', offset: '-04:00' },
  { value: 'America/Tortola', label: '토르톨라', offset: '-04:00' },
  { value: 'America/Thule', label: '툴레', offset: '-04:00' },
  { value: 'America/Porto_Velho', label: '포르토벨료', offset: '-04:00' },
  { value: 'America/Port_of_Spain', label: '포트오브스페인', offset: '-04:00' },
  { value: 'America/Puerto_Rico', label: '푸에르토리코', offset: '-04:00' },
  { value: 'America/Halifax', label: '핼리팩스', offset: '-04:00' },
  { value: 'America/Guayaquil', label: '과야킬', offset: '-05:00' },
  { value: 'America/Grand_Turk', label: '그랜드 터크', offset: '-05:00' },
  { value: 'America/Nassau', label: '나소', offset: '-05:00' },
  { value: 'America/New_York', label: '뉴욕', offset: '-05:00' },
  { value: 'America/Detroit', label: '디트로이트', offset: '-05:00' },
  { value: 'America/Louisville', label: '루이빌', offset: '-05:00' },
  { value: 'America/Lima', label: '리마', offset: '-05:00' },
  { value: 'America/Bogota', label: '보고타', offset: '-05:00' },
  { value: 'America/Eirunepe', label: '아이루네페', offset: '-05:00' },
  { value: 'America/Iqaluit', label: '이칼루이트', offset: '-05:00' },
  { value: 'America/Indiana/Marengo', label: '인디애나주, 머렝고', offset: '-05:00' },
  { value: 'America/Indiana/Vevay', label: '인디애나주, 비비', offset: '-05:00' },
  { value: 'America/Indiana/Vincennes', label: '인디애나주, 빈센스', offset: '-05:00' },
  { value: 'America/Indiana/Winamac', label: '인디애나주, 위너맥', offset: '-05:00' },
  { value: 'America/Indiana/Petersburg', label: '인디애나주, 피츠버그', offset: '-05:00' },
  { value: 'America/Indianapolis', label: '인디애나폴리스', offset: '-05:00' },
  { value: 'America/Jamaica', label: '자메이카', offset: '-05:00' },
  { value: 'America/Cancun', label: '칸쿤', offset: '-05:00' },
  { value: 'America/Cayman', label: '케이맨', offset: '-05:00' },
  { value: 'America/Kentucky/Monticello', label: '켄터키주, 몬티첼로', offset: '-05:00' },
  { value: 'America/Coral_Harbour', label: '코랄하버', offset: '-05:00' },
  { value: 'America/Toronto', label: '토론토', offset: '-05:00' },
  { value: 'America/Panama', label: '파나마', offset: '-05:00' },
  { value: 'America/Port-au-Prince', label: '포르토프랭스', offset: '-05:00' },
  { value: 'America/Havana', label: '하바나', offset: '-05:00' },
  { value: 'America/Rio_Branco', label: '히우 브랑쿠', offset: '-05:00' },
  { value: 'America/Guatemala', label: '과테말라', offset: '-06:00' },
  { value: 'America/North_Dakota/New_Salem', label: '노스다코타주, 뉴살렘', offset: '-06:00' },
  { value: 'America/North_Dakota/Beulah', label: '노스다코타주, 베라', offset: '-06:00' },
  { value: 'America/Rankin_Inlet', label: '랭킹 인렛', offset: '-06:00' },
  { value: 'America/Regina', label: '리자이나', offset: '-06:00' },
  { value: 'America/Resolute', label: '리졸루트', offset: '-06:00' },
  { value: 'America/Managua', label: '마나과', offset: '-06:00' },
  { value: 'America/Matamoros', label: '마타모로스', offset: '-06:00' },
  { value: 'America/Menominee', label: '메노미니', offset: '-06:00' },
  { value: 'America/Merida', label: '메리다', offset: '-06:00' },
  { value: 'America/Mexico_City', label: '멕시코 시티', offset: '-06:00' },
  { value: 'America/Monterrey', label: '몬테레이', offset: '-06:00' },
  { value: 'America/Bahia_Banderas', label: '바이아 반데라스', offset: '-06:00' },
  { value: 'America/Belize', label: '벨리즈', offset: '-06:00' },
  { value: 'America/Swift_Current', label: '스위프트커런트', offset: '-06:00' },
  { value: 'America/Chicago', label: '시카고', offset: '-06:00' },
  { value: 'America/El_Salvador', label: '엘살바도르', offset: '-06:00' },
  { value: 'America/Ojinaga', label: '오히나가', offset: '-06:00' },
  { value: 'America/Winnipeg', label: '위니펙', offset: '-06:00' },
  { value: 'America/Indiana/Knox', label: '인디애나주, 녹스', offset: '-06:00' },
  { value: 'America/Indiana/Tell_City', label: '인디애나주, 텔시티', offset: '-06:00' },
  { value: 'America/North_Dakota/Center', label: '중부, 노스다코타', offset: '-06:00' },
  { value: 'America/Chihuahua', label: '치와와', offset: '-06:00' },
  { value: 'America/Costa_Rica', label: '코스타리카', offset: '-06:00' },
  { value: 'America/Tegucigalpa', label: '테구시갈파', offset: '-06:00' },
  { value: 'America/Denver', label: '덴버', offset: '-07:00' },
  { value: 'America/Dawson', label: '도슨', offset: '-07:00' },
  { value: 'America/Dawson_Creek', label: '도슨크릭', offset: '-07:00' },
  { value: 'America/Mazatlan', label: '마사틀란', offset: '-07:00' },
  { value: 'America/Boise', label: '보이시', offset: '-07:00' },
  { value: 'America/Ciudad_Juarez', label: '시우다드후아레스', offset: '-07:00' },
  { value: 'America/Edmonton', label: '에드먼턴', offset: '-07:00' },
  { value: 'America/Hermosillo', label: '에르모시요', offset: '-07:00' },
  { value: 'America/Inuvik', label: '이누빅', offset: '-07:00' },
  { value: 'America/Cambridge_Bay', label: '케임브리지 베이', offset: '-07:00' },
  { value: 'America/Creston', label: '크레스톤', offset: '-07:00' },
  { value: 'America/Fort_Nelson', label: '포트 넬슨', offset: '-07:00' },
  { value: 'America/Phoenix', label: '피닉스', offset: '-07:00' },
  { value: 'America/Whitehorse', label: '화이트호스', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: '로스앤젤레스', offset: '-08:00' },
  { value: 'America/Vancouver', label: '벤쿠버', offset: '-08:00' },
  { value: 'America/Tijuana', label: '티후아나', offset: '-08:00' },
  { value: 'America/Nome', label: '놈', offset: '-09:00' },
  { value: 'America/Metlakatla', label: '메틀라카틀라', offset: '-09:00' },
  { value: 'America/Sitka', label: '싯카', offset: '-09:00' },
  { value: 'America/Anchorage', label: '앵커리지', offset: '-09:00' },
  { value: 'America/Yakutat', label: '야쿠타트', offset: '-09:00' },
  { value: 'America/Juneau', label: '주노', offset: '-09:00' },
  { value: 'America/Adak', label: '에이닥', offset: '-10:00' },
  { value: 'America/St_Johns', label: '세인트존스', offset: '-3:30' },
  { value: 'America/Danmarkshavn', label: '덴마크샤븐', offset: '+00:00' },
  
  // 태평양
  { value: 'Pacific/Easter', label: '이스터 섬', offset: '-05:00' },
  { value: 'Pacific/Galapagos', label: '갈라파고스', offset: '-06:00' },
  { value: 'Pacific/Pitcairn', label: '핏케언', offset: '-08:00' },
  { value: 'Pacific/Gambier', label: '감비어', offset: '-09:00' },
  { value: 'Pacific/Rarotonga', label: '라로통가', offset: '-10:00' },
  { value: 'Pacific/Tahiti', label: '타히티', offset: '-10:00' },
  { value: 'Pacific/Honolulu', label: '호놀룰루', offset: '-10:00' },
  { value: 'Pacific/Niue', label: '니우에', offset: '-11:00' },
  { value: 'Pacific/Midway', label: '미드웨이', offset: '-11:00' },
  { value: 'Pacific/Pago_Pago', label: '파고파고', offset: '-11:00' },
  { value: 'Pacific/Marquesas', label: '마퀘사스', offset: '-9:30' },
  { value: 'Pacific/Palau', label: '팔라우', offset: '+09:00' },
  { value: 'Pacific/Guam', label: '괌', offset: '+10:00' },
  { value: 'Pacific/Saipan', label: '사이판', offset: '+10:00' },
  { value: 'Pacific/Truk', label: '트루크', offset: '+10:00' },
  { value: 'Pacific/Port_Moresby', label: '포트모르즈비', offset: '+10:00' },
  { value: 'Pacific/Guadalcanal', label: '과달카날', offset: '+11:00' },
  { value: 'Pacific/Noumea', label: '누메아', offset: '+11:00' },
  { value: 'Pacific/Bougainville', label: '부갱빌', offset: '+11:00' },
  { value: 'Pacific/Efate', label: '에파테', offset: '+11:00' },
  { value: 'Pacific/Kosrae', label: '코스레', offset: '+11:00' },
  { value: 'Pacific/Ponape', label: '포나페', offset: '+11:00' },
  { value: 'Pacific/Nauru', label: '나우루', offset: '+12:00' },
  { value: 'Pacific/Norfolk', label: '노퍽', offset: '+12:00' },
  { value: 'Pacific/Majuro', label: '마주로', offset: '+12:00' },
  { value: 'Pacific/Wallis', label: '월리스', offset: '+12:00' },
  { value: 'Pacific/Wake', label: '웨이크', offset: '+12:00' },
  { value: 'Pacific/Kwajalein', label: '콰잘렌', offset: '+12:00' },
  { value: 'Pacific/Tarawa', label: '타라와', offset: '+12:00' },
  { value: 'Pacific/Funafuti', label: '푸나푸티', offset: '+12:00' },
  { value: 'Pacific/Fiji', label: '피지', offset: '+12:00' },
  { value: 'Pacific/Apia', label: '아피아', offset: '+13:00' },
  { value: 'Pacific/Auckland', label: '오클랜드', offset: '+13:00' },
  { value: 'Pacific/Enderbury', label: '칸톤', offset: '+13:00' },
  { value: 'Pacific/Tongatapu', label: '통가타푸', offset: '+13:00' },
  { value: 'Pacific/Fakaofo', label: '파카오푸', offset: '+13:00' },
  { value: 'Pacific/Chatham', label: '채텀', offset: '+13:45' },
  { value: 'Pacific/Kiritimati', label: '키리티마티', offset: '+14:00' },
  
  // 오세아니아
  { value: 'Australia/Perth', label: '퍼스', offset: '+08:00' },
  { value: 'Australia/Lindeman', label: '린데만', offset: '+10:00' },
  { value: 'Australia/Brisbane', label: '브리스베인', offset: '+10:00' },
  { value: 'Australia/Broken_Hill', label: '브로컨힐', offset: '+10:30' },
  { value: 'Australia/Adelaide', label: '애들레이드', offset: '+10:30' },
  { value: 'Australia/Lord_Howe', label: '로드 하우', offset: '+11:00' },
  { value: 'Australia/Melbourne', label: '멜버른', offset: '+11:00' },
  { value: 'Australia/Sydney', label: '시드니', offset: '+11:00' },
  { value: 'Australia/Hobart', label: '호바트', offset: '+11:00' },
  { value: 'Australia/Eucla', label: '유클라', offset: '+8:45' },
  { value: 'Australia/Darwin', label: '다윈', offset: '+9:30' },
  
  // 아프리카
  { value: 'Africa/Nouakchott', label: '누악쇼트', offset: '+00:00' },
  { value: 'Africa/Dakar', label: '다카르', offset: '+00:00' },
  { value: 'Africa/Lome', label: '로메', offset: '+00:00' },
  { value: 'Africa/Monrovia', label: '몬로비아', offset: '+00:00' },
  { value: 'Africa/Bamako', label: '바마코', offset: '+00:00' },
  { value: 'Africa/Banjul', label: '반줄', offset: '+00:00' },
  { value: 'Africa/Bissau', label: '비사우', offset: '+00:00' },
  { value: 'Africa/Sao_Tome', label: '상투메', offset: '+00:00' },
  { value: 'Africa/Abidjan', label: '아비장', offset: '+00:00' },
  { value: 'Africa/Accra', label: '아크라', offset: '+00:00' },
  { value: 'Africa/Ouagadougou', label: '와가두구', offset: '+00:00' },
  { value: 'Africa/Conakry', label: '코나크리', offset: '+00:00' },
  { value: 'Africa/Freetown', label: '프리타운', offset: '+00:00' },
  { value: 'Africa/Niamey', label: '니아메', offset: '+01:00' },
  { value: 'Africa/Douala', label: '두알라', offset: '+01:00' },
  { value: 'Africa/Lagos', label: '라고스', offset: '+01:00' },
  { value: 'Africa/Luanda', label: '루안다', offset: '+01:00' },
  { value: 'Africa/Libreville', label: '리브르빌', offset: '+01:00' },
  { value: 'Africa/Malabo', label: '말라보', offset: '+01:00' },
  { value: 'Africa/Bangui', label: '방기', offset: '+01:00' },
  { value: 'Africa/Brazzaville', label: '브라자빌', offset: '+01:00' },
  { value: 'Africa/Ceuta', label: '세우타', offset: '+01:00' },
  { value: 'Africa/Algiers', label: '알제', offset: '+01:00' },
  { value: 'Africa/Ndjamena', label: '엔자메나', offset: '+01:00' },
  { value: 'Africa/El_Aaiun', label: '엘아이운', offset: '+01:00' },
  { value: 'Africa/Casablanca', label: '카사블랑카', offset: '+01:00' },
  { value: 'Africa/Kinshasa', label: '킨샤사', offset: '+01:00' },
  { value: 'Africa/Tunis', label: '튀니스', offset: '+01:00' },
  { value: 'Africa/Porto-Novo', label: '포르토노보', offset: '+01:00' },
  { value: 'Africa/Gaborone', label: '가보로네', offset: '+02:00' },
  { value: 'Africa/Lubumbashi', label: '루붐바시', offset: '+02:00' },
  { value: 'Africa/Lusaka', label: '루사카', offset: '+02:00' },
  { value: 'Africa/Maseru', label: '마세루', offset: '+02:00' },
  { value: 'Africa/Maputo', label: '마푸토', offset: '+02:00' },
  { value: 'Africa/Bujumbura', label: '부줌부라', offset: '+02:00' },
  { value: 'Africa/Blantyre', label: '블랜타이어', offset: '+02:00' },
  { value: 'Africa/Windhoek', label: '빈트후크', offset: '+02:00' },
  { value: 'Africa/Johannesburg', label: '요하네스버그', offset: '+02:00' },
  { value: 'Africa/Mbabane', label: '음바바네', offset: '+02:00' },
  { value: 'Africa/Juba', label: '주바', offset: '+02:00' },
  { value: 'Africa/Khartoum', label: '카르툼', offset: '+02:00' },
  { value: 'Africa/Cairo', label: '카이로', offset: '+02:00' },
  { value: 'Africa/Kigali', label: '키갈리', offset: '+02:00' },
  { value: 'Africa/Tripoli', label: '트리폴리', offset: '+02:00' },
  { value: 'Africa/Harare', label: '하라레', offset: '+02:00' },
  { value: 'Africa/Nairobi', label: '나이로비', offset: '+03:00' },
  { value: 'Africa/Dar_es_Salaam', label: '다르에스살람', offset: '+03:00' },
  { value: 'Africa/Mogadishu', label: '모가디슈', offset: '+03:00' },
  { value: 'Africa/Addis_Ababa', label: '아디스아바바', offset: '+03:00' },
  { value: 'Africa/Asmera', label: '아스메라', offset: '+03:00' },
  { value: 'Africa/Djibouti', label: '지부티', offset: '+03:00' },
  { value: 'Africa/Kampala', label: '캄팔라', offset: '+03:00' },
  
  // 기타
  { value: 'Atlantic/Azores', label: '아조레스', offset: '-01:00' },
  { value: 'Atlantic/Cape_Verde', label: '카보 베르데', offset: '-01:00' },
  { value: 'Atlantic/South_Georgia', label: '사우스조지아', offset: '-02:00' },
  { value: 'Atlantic/Stanley', label: '스탠리', offset: '-03:00' },
  { value: 'Atlantic/Bermuda', label: '버뮤다', offset: '-04:00' },
  { value: 'Atlantic/Reykjavik', label: '레이캬비크', offset: '+00:00' },
  { value: 'Atlantic/Madeira', label: '마데이라', offset: '+00:00' },
  { value: 'Atlantic/St_Helena', label: '세인트 헬레나', offset: '+00:00' },
  { value: 'Atlantic/Canary', label: '카나리아 제도', offset: '+00:00' },
  { value: 'Atlantic/Faeroe', label: '페로 제도', offset: '+00:00' },
  { value: 'Antarctica/Rothera', label: '로데라', offset: '-03:00' },
  { value: 'Antarctica/Palmer', label: '파머', offset: '-03:00' },
  { value: 'Antarctica/Troll', label: '트롤', offset: '+00:00' },
  { value: 'Antarctica/Syowa', label: '쇼와', offset: '+03:00' },
  { value: 'Antarctica/Mawson', label: '모슨', offset: '+05:00' },
  { value: 'Antarctica/Vostok', label: '보스토크', offset: '+05:00' },
  { value: 'Antarctica/Davis', label: '데이비스', offset: '+07:00' },
  { value: 'Antarctica/Casey', label: '케이시', offset: '+08:00' },
  { value: 'Antarctica/DumontDUrville', label: '뒤몽 뒤르빌', offset: '+10:00' },
  { value: 'Antarctica/Macquarie', label: '맥쿼리', offset: '+11:00' },
  { value: 'Antarctica/McMurdo', label: '맥머도', offset: '+13:00' },
  { value: 'Arctic/Longyearbyen', label: '롱이어비엔', offset: '+01:00' },
  { value: 'Indian/Mayotte', label: '메요트', offset: '+03:00' },
  { value: 'Indian/Antananarivo', label: '안타나나리보', offset: '+03:00' },
  { value: 'Indian/Comoro', label: '코모로', offset: '+03:00' },
  { value: 'Indian/Reunion', label: '레위니옹', offset: '+04:00' },
  { value: 'Indian/Mahe', label: '마헤', offset: '+04:00' },
  { value: 'Indian/Mauritius', label: '모리셔스', offset: '+04:00' },
  { value: 'Indian/Maldives', label: '몰디브', offset: '+05:00' },
  { value: 'Indian/Kerguelen', label: '케르켈렌', offset: '+05:00' },
  { value: 'Indian/Chagos', label: '차고스', offset: '+06:00' },
  { value: 'Indian/Christmas', label: '크리스마스', offset: '+07:00' },
  { value: 'Indian/Cocos', label: '코코스', offset: '+6:30' },
  { value: 'Etc/UTC', label: '알 수 없는 장소', offset: '+00:00' }
];

